import React, { useEffect, useState } from "react";
import { Box, Paper, Typography, Grid, Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { useCreateUser } from "../../hooks/users/useCreateUser";
import { useUpdateUser } from "../../hooks/users/useUpdateUser";
import FormInput from "../inputs/FormInput";
import { useGetRoles } from "../../hooks/roles/useGetRoles";

const FormAddEditUser = ({ currentData, isEdit = false, onSubmitSuccess }) => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng.startsWith("ar");

    // Form States
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [passwordConfirmation, setPasswordConfirmation] = useState("");
    const [selectedRoles, setSelectedRoles] = useState([]);

    // Hooks
    const { createUserMutate, isPending: isCreating } = useCreateUser();
    const { updateUserMutate, isPending: isUpdating } = useUpdateUser();
    const { roles: rolesOptions } = useGetRoles({ per_page: 100 });

    const loadingSubmit = isCreating || isUpdating;

    // Fill form data in edit mode
    useEffect(() => {
        if (isEdit && currentData) {
            setName(currentData?.name || "");
            setEmail(currentData?.email || "");
            setSelectedRoles(currentData?.roles?.map(r => Number(r.id)) || []);
            // Password not filled for security
        }
    }, [isEdit, currentData]);

    const handleSubmit = (e) => {
        e.preventDefault();

        // Validate password confirmation in edit mode
        if (isEdit && password && !passwordConfirmation) {
            toast.error(isArabic ? "يرجى إدخال تأكيد كلمة المرور" : "Please confirm your password");
            return;
        }
        if (isEdit && password && password !== passwordConfirmation) {
            toast.error(isArabic ? "كلمة المرور وتأكيدها غير متطابقتين" : "Passwords do not match");
            return;
        }

        const payload = {
            name,
            email,
            roles: selectedRoles.map(r => Number(r)), // Send as array of numeric IDs
        };

        if (!isEdit) {
            payload.password = password;
            payload.password_confirmation = passwordConfirmation;
        } else if (password) {
            // Only send password fields together if password is provided
            payload.password = password;
            payload.password_confirmation = passwordConfirmation;
        }

        if (isEdit) {
            updateUserMutate({ id: currentData.id, data: payload });
        } else {
            createUserMutate(payload);
        }
    };

    return (
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
            <Paper sx={{ p: 3, borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom>
                    {isEdit ? (isArabic ? "تعديل المستخدم" : "Edit User") : (isArabic ? "إضافة مستخدم جديد" : "Add New User")}
                </Typography>

                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? "الاسم" : "Name"}
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            required
                            fullWidth
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? "البريد الإلكتروني" : "Email"}
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            fullWidth
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? "الأدوار" : "Roles"}
                            type="multi_select"
                            selectedValue={selectedRoles}
                            handleSelectChange={(e) => setSelectedRoles(e.target.value.map(v => Number(v)))}
                            options={rolesOptions || []}
                            required
                            fullWidth
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? "كلمة المرور" : "Password"}
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required={!isEdit}
                            fullWidth
                        />
                    </Grid>

                    {/* Confirm password: required in create, shown in edit when password is filled */}
                    {(!isEdit || password) && (
                        <Grid item xs={12} md={6}>
                            <FormInput
                                label={isArabic ? "تأكيد كلمة المرور" : "Confirm Password"}
                                type="password"
                                value={passwordConfirmation}
                                onChange={(e) => setPasswordConfirmation(e.target.value)}
                                required={!isEdit || Boolean(password)}
                                fullWidth
                            />
                        </Grid>
                    )}
                </Grid>

                <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={loadingSubmit}
                        sx={{ minWidth: 120 }}
                    >
                        {loadingSubmit ? (isArabic ? "جاري الحفظ..." : "Saving...") : (isArabic ? "حفظ" : "Save")}
                    </Button>
                </Box>
            </Paper>
        </Box>
    );
};

export default FormAddEditUser;